import { useEffect, useRef, useMemo } from 'react';
import { X, Save } from 'lucide-react';
import SearchableSelect from '../ui/SearchableSelect';
import { useView } from '../../contexts/ViewContext';
import { usePortalMode } from '../../hooks/usePortalMode';

interface FilterState {
  dateFrom: string;
  dateTo: string;
  supplier: string;
  customer: string;
  site: string;
  currency: string;
  supplierGroup: string;
  strategicOnly: boolean;
  supplierType: string;
  commodityGroup: string;
  commodityType: string;
  // CR-3932 to CR-3937
  siteCountry: string;
  supplierCountry: string;
  supplierCode: string;
  orderType: string;
  warehouse: string;
  dataMode: 'historical' | 'live';
  // CR-4349: Division filter
  division: string;
  // CR-4800: Compliance-specific filters
  approvalStage: string;
  riskCategory: string;
}

interface DashboardFiltersProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  suppliers: Array<{ id: number; name: string; group?: number | null }>;
  customers: Array<{ id: number; name: string }>;
  sites: Array<{ id: number; name: string; country?: string; group_id?: number | null; group?: number | null }>;
  warehouses: Array<{ id: number; name: string; site: number }>;
  supplierGroups: Array<{ id: number; name: string }>;
  commodityGroups: Array<{ id: number; name: string }>;
  commodityTypes: Array<{ id: number; name: string; commodity_group?: number }>;
  // CR-4349: Division filter options (only shown when > 0)
  divisions: Array<{ id: number; name: string }>;
  preferencesCurrency: string;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  onShowSaveDialog: () => void;
  onAutoCollapse?: () => void;
  // CR-4800: when true, hide purchasing/logistics-specific filters that aren't
  // meaningful for the Compliance page and surface approval-stage + risk
  // filters that are.
  isComplianceMode?: boolean;
}

/** Format a Date as YYYY-MM-DD in local time (avoids UTC timezone shift). */
function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Computes the "Last 12 Months" date range: 12 complete months up to end of last month.
 * E.g. if today is April 9, 2026, returns { dateFrom: '2025-04-01', dateTo: '2026-03-31' }.
 */
export function getLast12MonthsRange(): { dateFrom: string; dateTo: string } {
  const today = new Date();
  const dateFrom = formatLocalDate(new Date(today.getFullYear() - 1, today.getMonth(), 1));
  const dateTo = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0));
  return { dateFrom, dateTo };
}

export function DashboardFilters({
  filters,
  setFilters,
  suppliers,
  customers: _customers,
  sites,
  warehouses = [],
  supplierGroups = [],
  commodityGroups = [],
  commodityTypes = [],
  divisions = [],
  preferencesCurrency: _preferencesCurrency,
  onApplyFilters,
  onResetFilters,
  onShowSaveDialog,
  onAutoCollapse: _onAutoCollapse,
  isComplianceMode = false,
}: DashboardFiltersProps) {
  // CR-4577: Filter site/company options based on view context
  const { viewLevel, selectedCompanyId, selectedGroupId } = useView();
  // CR-4887: For supplier users the view context (selectedCompanyId,
  // selectedGroupId) refers to the SUPPLIER's own hierarchy, while the
  // Company Site / Division dropdowns now show CUSTOMER hierarchy. The
  // scope-narrowing and lock logic below assumes both sides are the same
  // hierarchy; bypass it for suppliers so the dropdowns stay user-driven
  // and so the locked Division value isn't fed back as a customer-side
  // ?group= filter (which collapsed dashboard data to zero).
  const { isSupplier: portalIsSupplier } = usePortalMode();

  const viewFilteredSites = useMemo(() => {
    if (portalIsSupplier) return sites;
    if (viewLevel === 'SITE' && selectedCompanyId) {
      return sites.filter((s) => s.id === selectedCompanyId);
    }
    if (viewLevel === 'COMPANY' && selectedCompanyId) {
      return sites.filter((s) => s.id === selectedCompanyId);
    }
    if (viewLevel === 'GROUP' && selectedGroupId && selectedGroupId > 0) {
      // Filter to sites within the selected group (division level)
      // Check both group_id (SiteSerializer) and group (CompanyListSerializer)
      const inGroup = sites.filter((s) => (s.group_id ?? s.group) === selectedGroupId);
      // If no direct matches, it's a root/parent group — show all sites
      return inGroup.length > 0 ? inGroup : sites;
    }
    return sites;
  }, [sites, viewLevel, selectedCompanyId, selectedGroupId, portalIsSupplier]);

  // CR-4577 rework: divisions should also honour the current view scope so
  // the dropdown can't offer a division that isn't in the active scope.
  // Division level → only the selected division. Site level → only the
  // parent division of the selected site. Group level → all divisions.
  const scopedDivisions = useMemo(() => {
    if (portalIsSupplier) return divisions;
    if (viewLevel === 'GROUP' && selectedGroupId && selectedGroupId > 0) {
      const match = divisions.filter((d) => d.id === selectedGroupId);
      return match.length > 0 ? match : divisions;
    }
    if ((viewLevel === 'SITE' || viewLevel === 'COMPANY') && selectedCompanyId) {
      const site = sites.find((s) => s.id === selectedCompanyId);
      const parentGroupId = site?.group_id ?? site?.group;
      if (parentGroupId) {
        const match = divisions.filter((d) => d.id === parentGroupId);
        if (match.length > 0) return match;
      }
    }
    return divisions;
  }, [divisions, viewLevel, selectedGroupId, selectedCompanyId, sites, portalIsSupplier]);

  // Locked when scope has pinned us to a single option — both the filter
  // should be disabled in the UI AND the actual filter value should match
  // the scope (auto-synced by the effect below).
  const isDivisionLocked = !portalIsSupplier && scopedDivisions.length === 1
    && ((viewLevel === 'GROUP' && !!selectedGroupId && selectedGroupId > 0)
      || ((viewLevel === 'SITE' || viewLevel === 'COMPANY') && !!selectedCompanyId));
  const isSiteLocked = !portalIsSupplier && (viewLevel === 'SITE' || viewLevel === 'COMPANY') && !!selectedCompanyId;
  const lockedDivisionValue = isDivisionLocked ? String(scopedDivisions[0].id) : '';
  const lockedSiteValue = isSiteLocked && selectedCompanyId ? String(selectedCompanyId) : '';

  // Sync filter values to the scope lock — prevents filters.division /
  // filters.site from lingering with a stale value from a previous scope.
  // CR-4577 round 3: also clear the filter on the locked → unlocked
  // transition (Site → Division / Group, Division → Group). Without that,
  // the Company Site field kept showing the user's previous site as
  // "selected" at Division level, and the stale filters.site silently
  // narrowed the Warehouse options to that old site's warehouses — the
  // "misleading filter" Sarah flagged in rework: the UI said a specific
  // site was selected while the dashboard data reflected the whole
  // division. We only clear on the transition (tracked via refs), so a
  // user's manual pick at an unlocked level survives until the next
  // scope change.
  const prevSiteLockedRef = useRef(isSiteLocked);
  const prevDivisionLockedRef = useRef(isDivisionLocked);
  useEffect(() => {
    const updates: Partial<FilterState> = {};
    const wasDivisionLocked = prevDivisionLockedRef.current;
    const wasSiteLocked = prevSiteLockedRef.current;
    prevDivisionLockedRef.current = isDivisionLocked;
    prevSiteLockedRef.current = isSiteLocked;

    if (isDivisionLocked) {
      if (filters.division !== lockedDivisionValue) {
        updates.division = lockedDivisionValue;
      }
    } else if (wasDivisionLocked && filters.division) {
      updates.division = '';
    }

    if (isSiteLocked) {
      if (filters.site !== lockedSiteValue) {
        updates.site = lockedSiteValue;
      }
    } else if (wasSiteLocked && filters.site) {
      updates.site = '';
    }

    if (Object.keys(updates).length > 0) {
      setFilters({ ...filters, ...updates });
    }
  }, [isDivisionLocked, isSiteLocked, lockedDivisionValue, lockedSiteValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter changes are NOT auto-applied. Users edit dates / dropdowns / text
  // freely; the dashboard refetches only when "Apply Filters" is clicked
  // (button at the bottom of the panel calls onApplyFilters). Auto-debounce
  // was removed because it kicked off a refetch every 3s on each keystroke,
  // which fired multiple expensive dashboard rebuilds while users were still
  // composing a date range. The Apply button is the single trigger.

  const setQuickDateRange = (range: string) => {
    const today = new Date();
    let dateFrom = '';
    let dateTo = '';

    switch (range) {
      case 'thisMonth':
        dateFrom = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1));
        dateTo = formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
        setFilters({ ...filters, dateFrom, dateTo, dataMode: 'live' });
        return;
      case 'lastMonth':
        dateFrom = formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
        dateTo = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0));
        break;
      case 'thisQuarter':
        const currentQuarter = Math.floor(today.getMonth() / 3);
        dateFrom = formatLocalDate(new Date(today.getFullYear(), currentQuarter * 3, 1));
        dateTo = formatLocalDate(new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0));
        break;
      case 'lastQuarter':
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
        const lastQuarterYear = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
        const adjustedQuarter = lastQuarter < 0 ? 3 : lastQuarter;
        dateFrom = formatLocalDate(new Date(lastQuarterYear, adjustedQuarter * 3, 1));
        dateTo = formatLocalDate(new Date(lastQuarterYear, (adjustedQuarter + 1) * 3, 0));
        break;
      case 'thisYear':
        dateFrom = formatLocalDate(new Date(today.getFullYear(), 0, 1));
        dateTo = formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
        break;
      case 'lastYear':
        dateFrom = formatLocalDate(new Date(today.getFullYear() - 1, 0, 1));
        dateTo = formatLocalDate(new Date(today.getFullYear() - 1, 11, 31));
        break;
      case 'last12Months': {
        // 12 completed months, excluding the current (incomplete) month
        const range = getLast12MonthsRange();
        dateFrom = range.dateFrom;
        dateTo = range.dateTo;
        break;
      }
    }

    setFilters({ ...filters, dateFrom, dateTo, dataMode: 'historical' });
  };

  // Check which quick date range is currently active
  const getQuickRangeDates = (range: string): { dateFrom: string; dateTo: string } | null => {
    const today = new Date();
    switch (range) {
      case 'last12Months': return getLast12MonthsRange();
      case 'thisMonth': return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
      case 'lastMonth': return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
      case 'lastQuarter': {
        const lq = Math.floor(today.getMonth() / 3) - 1;
        const lqy = lq < 0 ? today.getFullYear() - 1 : today.getFullYear();
        const aq = lq < 0 ? 3 : lq;
        return {
          dateFrom: formatLocalDate(new Date(lqy, aq * 3, 1)),
          dateTo: formatLocalDate(new Date(lqy, (aq + 1) * 3, 0)),
        };
      }
      case 'thisYear': return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), 0, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
      case 'lastYear': return {
        dateFrom: formatLocalDate(new Date(today.getFullYear() - 1, 0, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear() - 1, 11, 31)),
      };
      default: return null;
    }
  };

  const activeQuickRange = ['last12Months', 'thisMonth', 'lastMonth', 'lastQuarter', 'thisYear', 'lastYear']
    .find(range => {
      const dates = getQuickRangeDates(range);
      return dates && filters.dateFrom === dates.dateFrom && filters.dateTo === dates.dateTo;
    }) || null;

  const isLast12MonthsActive = activeQuickRange === 'last12Months';



  // Map of ISO country codes to display names
  const countryDisplayNames: Record<string, string> = {
    GB: 'United Kingdom', US: 'United States', DE: 'Germany', FR: 'France',
    CN: 'China', IN: 'India', JP: 'Japan', CA: 'Canada', AU: 'Australia',
    IT: 'Italy', ES: 'Spain', NL: 'Netherlands', SE: 'Sweden', CH: 'Switzerland',
    BR: 'Brazil', MX: 'Mexico', KR: 'South Korea', SG: 'Singapore',
    IE: 'Ireland', PL: 'Poland', BE: 'Belgium', AT: 'Austria', DK: 'Denmark',
    NO: 'Norway', FI: 'Finland', PT: 'Portugal', CZ: 'Czech Republic',
    NZ: 'New Zealand', ZA: 'South Africa', TH: 'Thailand', MY: 'Malaysia',
    TW: 'Taiwan', HK: 'Hong Kong', AE: 'United Arab Emirates', SA: 'Saudi Arabia',
    TR: 'Turkey', RO: 'Romania', HU: 'Hungary', PH: 'Philippines', VN: 'Vietnam',
  };

  // Derive unique site countries from view-filtered sites data
  const siteCountryOptions = Array.from(
    new Set(viewFilteredSites.map((s) => s.country).filter(Boolean))
  ).sort((a, b) => {
    const nameA = countryDisplayNames[a!] || a!;
    const nameB = countryDisplayNames[b!] || b!;
    return nameA.localeCompare(nameB);
  }) as string[];

  // Filter sites by view context + selected country
  const filteredSites = filters.siteCountry
    ? viewFilteredSites.filter((s) => s.country === filters.siteCountry)
    : viewFilteredSites;

  // When site country changes, clear site and warehouse if they no longer match
  const handleSiteCountryChange = (country: string) => {
    const updates: Partial<FilterState> = { siteCountry: country };
    if (country && filters.site) {
      const selectedSite = sites.find((s) => String(s.id) === filters.site);
      if (selectedSite && selectedSite.country !== country) {
        updates.site = '';
        updates.warehouse = '';
      }
    }
    setFilters({ ...filters, ...updates });
  };

  // Filter warehouses by view context, selected company, and/or country
  const filteredWarehouses = warehouses.filter((wh) => {
    // CR-4577: Respect view context — only show warehouses for sites in scope
    const siteInScope = viewFilteredSites.some((s) => s.id === wh.site);
    if (!siteInScope) return false;
    if (filters.site && String(wh.site) !== filters.site) return false;
    if (filters.siteCountry) {
      const whSite = sites.find((s) => s.id === wh.site);
      if (whSite && whSite.country !== filters.siteCountry) return false;
    }
    return true;
  });

  return (
    <div className="border-t bg-card">
      <div className="px-6 py-5">
        {/* Quick Date Range Buttons */}
        <div className="mb-6">
          <label className="text-sm font-semibold mb-3 block text-foreground">Quick Date Ranges</label>
          <div className="flex flex-wrap gap-2">
            {/* Last 12 Months - Default/Recommended */}
            <button
              onClick={() => setQuickDateRange('last12Months')}
              className={`px-4 py-2 text-sm font-medium border-2 rounded-md transition-colors ${
                isLast12MonthsActive
                  ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                  : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
              }`}
            >
              Last 12 Months (Default)
            </button>
            {['thisMonth', 'lastMonth', 'lastQuarter', 'thisYear', 'lastYear'].map((range) => {
              const isActive = activeQuickRange === range;
              return (
                <button
                  key={range}
                  onClick={() => setQuickDateRange(range)}
                  className={`px-4 py-2 text-sm font-medium border-2 rounded-md transition-colors ${
                    isActive
                      ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                      : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
                  }`}
                >
                  {range === 'thisMonth' ? 'This Month (Live)' : range.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-t mb-6"></div>

        {/* Filter Inputs - Row 1: Date Range, Company Country & Site (CR-4339: grouped together) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Date From</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            />
          </div>

          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Date To</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            />
          </div>

          {/* CR-3932 / CR-4339: Company Country (before Site) */}
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Company Country</label>
            <select
              value={filters.siteCountry}
              onChange={(e) => handleSiteCountryChange(e.target.value)}
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            >
              <option value="">All Countries</option>
              {siteCountryOptions.map((code) => (
                <option key={code} value={code}>{countryDisplayNames[code] || code}</option>
              ))}
            </select>
          </div>

          {/* Site (filtered by Company Country).
              CR-4577 rework: locked to the selected site when the view
              level pins a single site — dropdown is disabled and shows
              that site as the only option so the filter stops looking
              like an "All Company Sites" placeholder that does nothing. */}
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Company Site</label>
            {isSiteLocked ? (
              <div
                className="w-full px-3 py-2.5 border border-border rounded-md bg-muted/40 text-muted-foreground cursor-not-allowed"
                title="Locked by current view level"
              >
                {(filteredSites[0]?.name) || 'Selected site'}
              </div>
            ) : (
              <SearchableSelect
                options={filteredSites.map((s) => ({ value: String(s.id), label: s.name }))}
                value={filters.site}
                onChange={(val) => setFilters({ ...filters, site: val, warehouse: '' })}
                placeholder="All Company Sites"
              />
            )}
          </div>
        </div>

        {/* CR-4349: Division filter - only shown when divisions exist.
            CR-4577 rework: locked to the scope's division when viewing
            Division or Site level so a user can't pick another division
            that would have no effect on the dashboard. */}
        {divisions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Division</label>
            <select
              value={isDivisionLocked ? lockedDivisionValue : filters.division}
              onChange={(e) => setFilters({ ...filters, division: e.target.value })}
              disabled={isDivisionLocked}
              title={isDivisionLocked ? 'Locked by current view level' : undefined}
              className={`w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background ${
                isDivisionLocked ? 'bg-muted/40 text-muted-foreground cursor-not-allowed' : ''
              }`}
            >
              {!isDivisionLocked && <option value="">All Divisions</option>}
              {scopedDivisions.map((div) => (
                <option key={div.id} value={div.id}>{div.name}</option>
              ))}
            </select>
          </div>
        </div>
        )}

        {/* Filter Inputs - Row 2: Warehouse, Supplier Country, Supplier Group, Supplier.
            CR-4800: Warehouse / Supplier Group / Supplier Site are purchasing or
            logistics concepts that don't apply to compliance reporting. Only
            Supplier Country stays on the Compliance page. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* CR-3936 / CR-4339: Warehouse (grouped after Site) */}
          {!isComplianceMode && (
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Warehouse</label>
            <select
              value={filters.warehouse}
              onChange={(e) => setFilters({ ...filters, warehouse: e.target.value })}
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            >
              <option value="">All Warehouses</option>
              {filteredWarehouses.map((wh) => (
                <option key={wh.id} value={wh.id}>{wh.name}</option>
              ))}
            </select>
          </div>
          )}

          {/* CR-3933: Supplier Country.
              CR-4887 round 4: hide on supplier-portal pages — the dropdown
              is a hardcoded global list of seven countries, meaningless for
              a user who IS a supplier of one country. Their relevant
              "country" filter is Company Country (the customer's country),
              which is already dynamically derived from the accessible
              customer companies above. */}
          {!portalIsSupplier && (
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Supplier Country</label>
            <select
              value={filters.supplierCountry}
              onChange={(e) => setFilters({ ...filters, supplierCountry: e.target.value })}
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            >
              <option value="">All Countries</option>
              <option value="GB">United Kingdom</option>
              <option value="US">United States</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="CN">China</option>
              <option value="IN">India</option>
              <option value="JP">Japan</option>
            </select>
          </div>
          )}

          {!isComplianceMode && (
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Supplier Group</label>
            <SearchableSelect
              options={supplierGroups.map((g) => ({ value: String(g.id), label: g.name }))}
              value={filters.supplierGroup}
              onChange={(val) => {
                // CR-4885: Selecting a Supplier Group must narrow the
                // Supplier Site dropdown. Clear an existing site selection
                // if it doesn't belong to the newly chosen group, otherwise
                // the dashboard reads an inconsistent filter combo and
                // returns empty data.
                const next = { ...filters, supplierGroup: val };
                if (val && filters.supplier) {
                  const stillValid = suppliers.some(
                    (s) => String(s.id) === String(filters.supplier) && String(s.group ?? '') === val
                  );
                  if (!stillValid) next.supplier = '';
                }
                setFilters(next);
              }}
              placeholder="All Supplier Groups"
            />
          </div>
          )}

          {!isComplianceMode && (
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Supplier Site</label>
            <SearchableSelect
              // CR-4885: When a Supplier Group is selected, restrict the
              // site dropdown to that group's members only.
              options={(filters.supplierGroup
                ? suppliers.filter((s) => String(s.group ?? '') === filters.supplierGroup)
                : suppliers
              ).map((s) => ({ value: String(s.id), label: s.name }))}
              value={filters.supplier}
              onChange={(val) => setFilters({ ...filters, supplier: val })}
              placeholder="All Supplier Sites"
            />
          </div>
          )}

          {/* CR-4800: Compliance-specific filters — Approval Stage & Risk Category */}
          {isComplianceMode && (
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Approval Stage</label>
            <select
              value={filters.approvalStage}
              onChange={(e) => setFilters({ ...filters, approvalStage: e.target.value })}
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            >
              <option value="">All Stages</option>
              <option value="Not Invited">Not Invited</option>
              <option value="Invited">Invited</option>
              <option value="In Progress">In Progress</option>
              <option value="Submitted / Under Review">Submitted / Under Review</option>
              <option value="More Info Required">More Info Required</option>
              <option value="Approved">Approved</option>
              <option value="Declined">Declined</option>
              <option value="Contract Awarded">Contract Awarded</option>
            </select>
          </div>
          )}

          {isComplianceMode && (
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Risk Category</label>
            <select
              value={filters.riskCategory}
              onChange={(e) => setFilters({ ...filters, riskCategory: e.target.value })}
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            >
              <option value="">All Risk Levels</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          )}
        </div>

        {/* Filter Inputs - Row 3: Supplier Code, Supplier Type, Order Type, Commodity Group.
            CR-4800: Supplier Code / Supplier Type / Order Type are purchasing-
            or ERP-specific. None apply to compliance workflows. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* CR-3934: Supplier Code */}
          {!isComplianceMode && (
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Supplier Code</label>
            <input
              type="text"
              value={filters.supplierCode}
              onChange={(e) => setFilters({ ...filters, supplierCode: e.target.value })}
              placeholder="Enter supplier code..."
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            />
          </div>
          )}

          {!isComplianceMode && (
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Supplier Type</label>
            <select
              value={filters.supplierType}
              onChange={(e) => setFilters({ ...filters, supplierType: e.target.value })}
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            >
              <option value="">All Suppliers</option>
              <option value="internal">Internal Suppliers</option>
              <option value="external">External Suppliers</option>
            </select>
          </div>
          )}

          {/* CR-3935: Order Type */}
          {!isComplianceMode && (
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Order Type</label>
            <select
              value={filters.orderType}
              onChange={(e) => setFilters({ ...filters, orderType: e.target.value })}
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            >
              <option value="">All Order Types</option>
              <option value="scheduled">Scheduled</option>
              <option value="spot">Spot</option>
              <option value="blanket">Blanket</option>
              <option value="contract">Contract</option>
            </select>
          </div>
          )}

          {/* Commodity Group */}
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Commodity Group</label>
            <select
              value={filters.commodityGroup}
              onChange={(e) => setFilters({ ...filters, commodityGroup: e.target.value })}
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            >
              <option value="">All Commodity Groups</option>
              {commodityGroups.map((group) => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Filter Inputs - Row 4: Commodity Type + Strategic */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Commodity Type */}
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Commodity Type</label>
            <select
              value={filters.commodityType}
              onChange={(e) => setFilters({ ...filters, commodityType: e.target.value })}
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            >
              <option value="">All Commodity Types</option>
              {commodityTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          {/* Strategic Suppliers Checkbox */}
          <div className="flex items-center pt-7">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.strategicOnly}
                onChange={(e) => setFilters({ ...filters, strategicOnly: e.target.checked })}
                className="w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-2"
              />
              <span className="text-sm font-medium text-foreground">Strategic Suppliers Only</span>
            </label>
          </div>

          {/* Data Mode Toggle: Historical / Live.
              CR-4800: dataMode is an OTIF / purchasing concept; compliance data
              has no historical-vs-live split, so the toggle is hidden there. */}
          {!isComplianceMode && (
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">Data</label>
            <div className="flex">
              <button
                onClick={() => { if (activeQuickRange !== 'thisMonth') setFilters({ ...filters, dataMode: 'historical' }); }}
                disabled={activeQuickRange === 'thisMonth'}
                className={`px-4 py-2.5 text-sm font-medium rounded-l-md border transition-colors ${
                  activeQuickRange === 'thisMonth'
                    ? 'bg-muted text-muted-foreground border-border cursor-not-allowed opacity-50'
                    : filters.dataMode === 'historical'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:bg-muted'
                }`}
              >
                Historical
              </button>
              <button
                onClick={() => setFilters({ ...filters, dataMode: 'live' })}
                className={`px-4 py-2.5 text-sm font-medium rounded-r-md border-t border-r border-b transition-colors ${
                  filters.dataMode === 'live'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:bg-muted'
                }`}
              >
                Live
              </button>
            </div>
          </div>
          )}
        </div>

        {/* Filter Actions */}
        <div className="mt-6 pt-6 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onApplyFilters}
                className="px-6 py-2.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 font-medium shadow-sm transition-all"
              >
                Apply Filters
              </button>
              <button
                onClick={onResetFilters}
                className="px-6 py-2.5 border-2 border-border rounded-md hover:bg-muted flex items-center gap-2 font-medium transition-all"
              >
                <X className="h-4 w-4" />
                Reset
              </button>
              <button
                onClick={onShowSaveDialog}
                className="px-6 py-2.5 border-2 border-primary text-primary rounded-md hover:bg-primary/10 flex items-center gap-2 font-medium transition-all"
              >
                <Save className="h-4 w-4" />
                Save Filter
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
